import { z } from 'zod'

/**
 * Payments (docs/sales.spec.md, "Paying"). All but the last mirror the Prisma
 * enums `SalesPaymentKind`, `SalesPaymentState`, `SalesRecordMethod`,
 * `SalesRecordStatus`, `SalesRefundKind` and `SalesRefundStatus`; they must
 * stay in sync.
 */

export const SALES_PAYMENT_KINDS = ['installment', 'security_deposit'] as const
export const salesPaymentKindSchema = z.enum(SALES_PAYMENT_KINDS)
export type SalesPaymentKind = z.infer<typeof salesPaymentKindSchema>

/** Whether a payment is still owed; whether it's paid comes from its records. */
export const SALES_PAYMENT_STATES = ['scheduled', 'stopped', 'canceled'] as const
export const salesPaymentStateSchema = z.enum(SALES_PAYMENT_STATES)
export type SalesPaymentState = z.infer<typeof salesPaymentStateSchema>

export const SALES_RECORD_METHODS = ['card', 'bank', 'check', 'transfer', 'other'] as const
export const salesRecordMethodSchema = z.enum(SALES_RECORD_METHODS)
export type SalesRecordMethod = z.infer<typeof salesRecordMethodSchema>

/**
 * A payment the couple starts in the app. A subset of `SALES_RECORD_METHODS`:
 * the pay page, Stripe, and autopay all name these two, and only these two.
 */
export const ONLINE_PAYMENT_METHODS = ['card', 'bank'] as const satisfies readonly SalesRecordMethod[]
export const onlinePaymentMethodSchema = z.enum(ONLINE_PAYMENT_METHODS)
export type OnlinePaymentMethod = z.infer<typeof onlinePaymentMethodSchema>

/** A payment staff record by hand: a check, a wire, or anything else. */
export const OUTSIDE_PAYMENT_METHODS = ['check', 'transfer', 'other'] as const satisfies readonly SalesRecordMethod[]
export const outsidePaymentMethodSchema = z.enum(OUTSIDE_PAYMENT_METHODS)
export type OutsidePaymentMethod = z.infer<typeof outsidePaymentMethodSchema>

export const SALES_RECORD_METHOD_LABELS: Readonly<Record<SalesRecordMethod, string>> = {
  card: 'Card',
  bank: 'Bank transfer',
  check: 'Check',
  transfer: 'Wire or transfer',
  other: 'Other',
}

export const SALES_RECORD_STATUSES = ['pending', 'processing', 'succeeded', 'failed'] as const
export const salesRecordStatusSchema = z.enum(SALES_RECORD_STATUSES)
export type SalesRecordStatus = z.infer<typeof salesRecordStatusSchema>

/** A refund of any payment, or a security deposit returned after the event. */
export const SALES_REFUND_KINDS = ['refund', 'deposit_return'] as const
export const salesRefundKindSchema = z.enum(SALES_REFUND_KINDS)
export type SalesRefundKind = z.infer<typeof salesRefundKindSchema>

/** Pending and succeeded count as given back; a failed one doesn't. */
export const SALES_REFUND_STATUSES = ['pending', 'succeeded', 'failed'] as const
export const salesRefundStatusSchema = z.enum(SALES_REFUND_STATUSES)
export type SalesRefundStatus = z.infer<typeof salesRefundStatusSchema>

/** Where a payment stands, as people read it. Worked out, never stored. */
export const PAYMENT_STATUSES = ['upcoming', 'due', 'late', 'processing', 'paid', 'stopped', 'canceled'] as const
export const paymentStatusSchema = z.enum(PAYMENT_STATUSES)
export type PaymentStatus = z.infer<typeof paymentStatusSchema>

export const PAYMENT_STATUS_LABELS: Readonly<Record<PaymentStatus, string>> = {
  upcoming: 'Upcoming',
  due: 'Due',
  late: 'Late',
  processing: 'Processing',
  paid: 'Paid',
  stopped: 'Stopped',
  canceled: 'Canceled',
}
